import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import { identityServices } from '../identityServices'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { useUserStore, useUserAuthSessionStore } from '@/store'

export const useGetMe = () => {
  const { t } = useTranslation()
  const { token, isSessionOpen, removeSession } = useUserAuthSessionStore()
  const { clearUserData } = useUserStore()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: [QUERY_KEYS.IDENTITY_ME],
    queryFn: identityServices.getMe,
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: Boolean(token && isSessionOpen), // Only run if authenticated
  })

  const setUserData = useUserStore((state) => state.setUserData)

  useEffect(() => {
    if (query.data) {
      const fullName =
        `${query.data.first_name || ''} ${query.data.last_name || ''}`.trim()

      setUserData({
        userAuthenticated: {
          ...query.data,
          fullName: fullName || query.data.username || 'User', // Fallback to username or 'User'
          hasIdentityVerified: query.data.verify_state === 'VERIFIED',
        },
      })
      return
    }
    if (query.error) {
      // Check if the error is a 401 and navigate to root
      if ('status' in query.error && query.error.status === 401) {
        // Clear session and user data before navigation to prevent redirect loop
        removeSession()
        clearUserData()
        navigate('/', { replace: true })
        return
      }

      toast.error(t('api:errors.somethingWrong'))
    }
  }, [
    query.data,
    query.error,
    setUserData,
    t,
    navigate,
    removeSession,
    clearUserData,
  ])

  return query
}
